import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import 'react-tippy/dist/tippy.css';
import Tooltip from '../molecules/Tooltip';
import css from '../partials/Grid.css';
import TransparencyPopover from '../reportsWidgets/TransparencyPopover';
import DimensionDrilldownPopover from '../../containers/reportsWidgets/DimensionDrilldownPopover';
import DashboardDimensionDrilldownPopover from '../../containers/reportsWidgets/DashboardDimensionDrilldownPopover';
import DrilldownIcon from '../../resources/svg/report_drilldown.svg';
import withTranslate from '../../containers/widgets/withTranslate';
import { getSupportedAppNames, getUnsupportedAppTooltip } from '../../selectors/reports';
import {
    ADMON_ALIGNMENT_SOURCE_NAME,
    APP_FIELD,
    CREATIVE_IMAGE,
    FACEBOOK_CENSORING_MESSAGE_TRANSLATE,
    TRANSPARENCY_COLUMN_LIST,
} from '../../utils/reportsConsts';
import { getCellTooltipInfo, getTransparencySourceTypeText, isAdmonTransparencyCell } from '../../utils/reports';
import { parseSafeHtml } from '../../utils/render';
import {
    formatCell,
    getColConfidenceIntervalName,
    isFacebookCensoredByParams,
    isSupportedApp,
} from '../../utils/grids';
import CreativePreview from '../creatives/CreativePreview';

const GridGeneralCellRenderer = memo(
    ({
        node = {},
        valueFormatted = '',
        column = {},
        eGridCell = {},
        colDef = {},
        reportType,
        translate = () => {},
    }) => {
        const [showTransparencyPopover, setShowTransparencyPopover] = useState(false);
        const [showDrilldownPopover, setShowDrilldownPopover] = useState(false);

        const supportedAppNames = useSelector(state => getSupportedAppNames(state, reportType));
        const unsupportedAppTooltip = useSelector(state => getUnsupportedAppTooltip(state, reportType));

        const cellTooltipInfo = getCellTooltipInfo(valueFormatted, node.data, column.colId);
        const confidenceIntervalColName = getColConfidenceIntervalName(colDef);
        const isFacebookCensored = isFacebookCensoredByParams(node);
        const isUnsupportedAppValue = column.colId === APP_FIELD && !isSupportedApp(node, supportedAppNames);

        const { displayFormat } = colDef?.context || colDef || {};
        const confidenceIntervalDisplayFormat = displayFormat ? { ...displayFormat } : null;
        if (confidenceIntervalDisplayFormat?.type === 'number') {
            confidenceIntervalDisplayFormat.precision = 2;
        }

        // Source info for transparency cells
        let sourceInfo = { sourceType: '', drilldownUans: null, sourceLabelTypeClass: '' };
        if (node.data?.sources?.column_source?.[column.colId]) {
            const sourceType = node.data.sources.column_source[column.colId].source;
            sourceInfo = {
                sourceType,
                drilldownUans: node.data.sources.uans,
                sourceLabelTypeClass: css[`${sourceType}SourceLabel`],
            };
        }

        const handleClick = () => {
            setShowTransparencyPopover(true);
            const isAdmon = !TRANSPARENCY_COLUMN_LIST.includes(colDef.colId);
            window.mixpanel.track('Reports: Transparency Drilldown Click', { is_admon: isAdmon });
        };

        const handleRequestClose = () => setShowTransparencyPopover(false);
        const handleDrillDownRequestClose = () => setShowDrilldownPopover(false);

        const handleDrillDownIconClick = () => {
            setShowDrilldownPopover(true);
            window.mixpanel.track('Drilldown popper opened');
        };

        const isAdmonAlignmentRow = nodeData => nodeData?.data?.source === ADMON_ALIGNMENT_SOURCE_NAME;

        const isDashboardReport = () => {
            const { widgetQuery, reportDates } = colDef.context || {};
            return widgetQuery && reportDates;
        };

        const renderDrilldownPopover = () => {
            // Use dashboard-specific popover if this is a dashboard widget
            return isDashboardReport() ? (
                <DashboardDimensionDrilldownPopover
                    widgetMetadata={colDef.context.reportMetadata}
                    widgetQuery={colDef.context.widgetQuery}
                    reportDates={colDef.context.reportDates}
                    availableFilters={colDef.context.availableFilters}
                    displayUnenriched={colDef.context.displayUnenriched}
                    nodeData={node.data}
                    showPopover
                    anchorEl={eGridCell}
                    closePopover={handleDrillDownRequestClose}
                />
            ) : (
                <DimensionDrilldownPopover
                    column={column}
                    nodeData={node.data}
                    showPopover
                    anchorEl={eGridCell}
                    closePopover={handleDrillDownRequestClose}
                />
            );
        };

        const valueFormattedTranslated =
            isFacebookCensored && valueFormatted === FACEBOOK_CENSORING_MESSAGE_TRANSLATE
                ? translate(valueFormatted)
                : null;

        const { cellTooltipElement, isInterpolatedRecord } = cellTooltipInfo;
        const cellTooltipText = isUnsupportedAppValue ? unsupportedAppTooltip : null;

        const confidenceInterval = confidenceIntervalColName ? (
            <span className={css.tableMarginValue}>
                (± {formatCell(node.data[confidenceIntervalColName], confidenceIntervalDisplayFormat)})
            </span>
        ) : null;

        if (colDef.colId === CREATIVE_IMAGE || colDef.showRowGroup === CREATIVE_IMAGE) {
            return (
                // Since the ag-grid library doesn't have the translate context by default,
                // we must receive the translate as a prop
                <CreativePreview
                    translate={translate}
                    url={valueFormatted}
                    type={(node.data || node.allLeafChildren[0].data).creative_type}
                />
            );
        }

        if (
            (!isFacebookCensored && TRANSPARENCY_COLUMN_LIST.indexOf(column.colId) >= 0) ||
            isAdmonTransparencyCell(node, column)
        ) {
            const { sourceType, drilldownUans, sourceLabelTypeClass } = sourceInfo;

            const transparencyContent = (
                <div className={css.elipsableCellValue}>
                    {(cellTooltipText || cellTooltipElement) && (
                        <div className={classNames(css.markCell, { [css.markCellGreen]: isInterpolatedRecord })} />
                    )}
                    <span className={`${css.sourceLabel} ${sourceLabelTypeClass}`} onClick={handleClick} title="">
                        {sourceType ? getTransparencySourceTypeText(sourceType) : null}
                    </span>
                    <span>{valueFormatted}</span>
                    {confidenceInterval}
                    {showTransparencyPopover && drilldownUans && (
                        <TransparencyPopover
                            uansContent={drilldownUans}
                            column={column}
                            nodeData={node.data}
                            popupWidth={column.actualWidth}
                            handleRequestClose={handleRequestClose}
                            showPopover={showTransparencyPopover}
                            anchorEl={eGridCell}
                        />
                    )}
                </div>
            );

            return transparencyContent;
        }

        const regularContent = (
            <>
                {(cellTooltipText || cellTooltipElement) && <div className={css.markCell} />}
                <span className={classNames(css.elipsableCellValue, { [css.showDrillDownIcon]: showDrilldownPopover })}>
                    {colDef.context?.showDimensionDrilldown && !isAdmonAlignmentRow(node) && (
                        <span title={translate('STATIC.PAGES.REPORTS.DIMENSION_DRILLDOWN_ICON_TOOLTIP')}>
                            <div onClick={handleDrillDownIconClick} className={css.drilldownIcon}>
                                <DrilldownIcon />
                            </div>
                        </span>
                    )}
                    {showDrilldownPopover && renderDrilldownPopover()}
                    <span className={classNames({ [css.unsupportedCell]: isUnsupportedAppValue })}>
                        {valueFormattedTranslated || valueFormatted}
                    </span>
                    {confidenceInterval}
                </span>
            </>
        );

        if (cellTooltipElement || cellTooltipText?.text) {
            const tooltipTitle = cellTooltipElement ? (
                <span dangerouslySetInnerHTML={{ __html: parseSafeHtml(cellTooltipElement) }} />
            ) : (
                translate(cellTooltipText.text, cellTooltipText.params)
            );

            return (
                <Tooltip interactive delay={250} title={tooltipTitle} contentClass={css.cellTooltip}>
                    {regularContent}
                </Tooltip>
            );
        }

        return regularContent;
    }
);

GridGeneralCellRenderer.propTypes = {
    node: PropTypes.objectOf(PropTypes.any),
    valueFormatted: PropTypes.string,
    column: PropTypes.objectOf(PropTypes.any),
    eGridCell: PropTypes.objectOf(PropTypes.any),
    colDef: PropTypes.objectOf(PropTypes.any),
    reportType: PropTypes.string.isRequired,
    translate: PropTypes.func,
};

export default withTranslate(GridGeneralCellRenderer);
